import {
  Affix,
  Anchor,
  AppShell,
  Breadcrumbs,
  Burger,
  Button,
  Center,
  Divider,
  Flex,
  Group,
  Image,
  NavLink,
  Space,
  Stack,
  Text,
  Transition,
  UnstyledButton,
} from '@mantine/core';
import {
  useClickOutside,
  useDebouncedValue,
  useDisclosure,
  useElementSize,
  useHeadroom,
  useIdle,
  useInterval,
  useTimeout,
  useWindowScroll,
} from '@mantine/hooks';
import {
  modals,
} from '@mantine/modals';

import NorthIcon from '@mui/icons-material/North';

import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useMatches,
} from '@tanstack/react-router';
import React, {
  useEffect,
  useState,
} from 'react';
import {
  useAuth,
  withAuthenticationRequired,
  type WithAuthenticationRequiredProps,
} from 'react-oidc-context';

import styles from '#features/auth/_authenticated.module.scss';
import SpeciaLogo from '#resources/images/logos/speciaLogo.svg?react';
import {
  NotFoundError,
} from '#components/errors/404/NotFoundError';
import {
  BackgroundLetterAvatar,
} from '#src/components/ui/avatar/BackgroundLetterAvatar';
import {
  IdleSessionModal,
} from '#features/auth/session/IdleSessionModal';
import {
  AuthenticatedAside,
} from '#components/ui/nav/AuthenticatedAside';
import {
  type Accountant,
  type RootRouterAuthContext,
} from '#src/types/auth';
import {
  NonBreakingCharEnum,
} from '@specia-accountant/shared';
import {
  isCrumbType,
  type CrumbType,
  type MakeRouteMatchFromRoute,
} from '#src/types/routerContext';


const sessionIdleWarningSeconds = 60;

const protectedOutlet = () => {
  const headerHeight = 80;
  const auth = useAuth();

  const [
    isAdmin,
    setIsAdmin,
  ] = useState<boolean>(false);
  useEffect(() => {
    const hasAdminGroup: boolean = (auth.user?.profile.groups as string[]).includes('client-admin') ?? false;
    setIsAdmin(hasAdminGroup);
  }, [
    auth.user
  ]);
  const [
    navbarMobileOpened,
    navbarMobileHandlers,
  ] = useDisclosure(false);
  const [
    navbarDesktopOpened,
    navbarDesktopHandlers,
  ] = useDisclosure(false);
  const [
    asideMobileOpened,
    asideMobileHandlers,
  ] = useDisclosure(false);
  const [
    asideDesktopOpened,
    asideDesktopHandlers,
  ] = useDisclosure(false);

  const pinnedHeader = useHeadroom({
    fixedAt: Math.floor(headerHeight * 1.5),
  });
  const [debouncedPinnedHeader] = useDebouncedValue(pinnedHeader, 500);

  function isHeaderCollapsed() {
    const asideOpen = (asideDesktopOpened || asideMobileOpened);
    const navbarOpen = (navbarDesktopOpened || navbarMobileOpened);
    return (!debouncedPinnedHeader) || asideOpen || navbarOpen;
  }

  const asideRef = useClickOutside(() => {
    asideDesktopHandlers.close();
    asideMobileHandlers.close();
  });
  const navbarRef = useClickOutside(() => {
    navbarDesktopHandlers.close();
    navbarMobileHandlers.close();
  });
  const [
    scroll,
    scrollTo,
  ] = useWindowScroll();
  const crumbsSize = useElementSize();
  function selectHeaderContext(
    matches: MakeRouteMatchFromRoute<typeof Route>[],
  ) {
    return matches.map(({
      pathname,
      context,
    }) => {
      return {
        header: context.header,
        path: pathname,
      };
    });
  }
  const headerMatches = useMatches({
    select: selectHeaderContext,
  });

  function selectCrumbs(matches: MakeRouteMatchFromRoute<typeof Route>[]) {
    const crumbs = matches
      .filter(match => match.context.crumb)
      .map(({
        pathname, context,
      }) => {
        if (isCrumbType(context.crumb)) {
          return {
            href: pathname + context.crumb.href,
            label: context.crumb.label,
          } as CrumbType;
        } else {
          return {
            href: pathname,
            label: context.crumb,
          } as CrumbType;
        }
      })
      .filter((obj1: CrumbType, i, arr) => {
        return arr.findIndex((obj2: CrumbType) => {
          return (obj2.label === obj1.label);
        }) === i;
      }).map((item, index, _array) => {
        return (
          <Anchor
            component={ Link }
            to={ item.href }
            key={ index }
          >
            { item.label }
          </Anchor>
        );
      });
    crumbs.pop();
    return crumbs;
  }
  const matchesWithCrumbs = useMatches({
    select: selectCrumbs,
  });
  return (
    <>
      <AppShell
        className={ styles.layoutShell }
        transitionDuration={ 750 }
        transitionTimingFunction="ease-in-out"
        withBorder={ false }
        header={ {
          height: headerHeight,
          collapsed: isHeaderCollapsed(),
          offset: true,
        } }
        navbar={ {
          width: 300,
          breakpoint: 'md',
          collapsed: {
            mobile: !navbarMobileOpened,
            desktop: !navbarDesktopOpened,
          },
        } }
        aside={ {
          width: 300,
          breakpoint: 'md',
          collapsed: {
            mobile: !asideMobileOpened,
            desktop: !asideDesktopOpened,
          },
        } }
        padding="md"
      >
        <AppShell.Header
          className={ styles.layoutHeader }
          px="md"
        >
          <div
            className={ styles.headerEdge }
          >
          </div>
          <Flex
            ref={ crumbsSize.ref }
            justify="space-between"
            align="center"
          >
            <SpeciaLogo
              width="60px"
              height="60px"
            />
            <Center>
              <Burger
                aria-label="Toggle left navigation menu opened"
                opened={ navbarMobileOpened }
                onClick={ navbarMobileHandlers.toggle }
                hiddenFrom="md"
                size="md"
              />
            </Center>
            <Space
              w="xl"
              visibleFrom="lg"
            />
            <Breadcrumbs
              separator="→"
              separatorMargin="xs"
              visibleFrom="xl"
            // mt="xs"
            >
            {...matchesWithCrumbs}
            </Breadcrumbs>
          </Flex>
          <Flex
            justify="space-between"
            align="center"
          >
            { headerMatches.at(-1)?.header }
          </Flex>
          <Flex
            justify="flex-end"
            align="center"
            p="md"
            w={ crumbsSize.width }
          >
            <UnstyledButton
              aria-label="Toggle left navigation menu opened"
              onClick={ () => {
                asideDesktopHandlers.toggle();
                asideMobileHandlers.toggle();
              } }
            >
              <BackgroundLetterAvatar
                username={ `${auth.user?.profile?.given_name} ${auth.user?.profile?.family_name}` }
              />
            </UnstyledButton>
          </Flex>
        </AppShell.Header>
        <AppShell.Navbar
          className={ styles.layoutNavbar }
          ref={ navbarRef }
          p="0"
        // bg="secondary.9"
        >
          <Flex
            justify="flex-end"
            align="center"
          >
            <Center
              h={ headerHeight }
              px="md"
            >
              <Burger
                aria-label="Toggle left navigation menu opened"
                opened={ navbarDesktopOpened }
                onClick={ navbarDesktopHandlers.toggle }
                visibleFrom="md"
                size="md"
              />
              <Burger
                aria-label="Toggle left navigation menu opened"
                opened={ navbarMobileOpened }
                onClick={ navbarMobileHandlers.toggle }
                hiddenFrom="md"
                size="md"
              />
            </Center>
          </Flex>
        </AppShell.Navbar>
        <AppShell.Main
          id="app-shell-main"
          className={ styles.layoutMain }
        >
          { /* <Space h={pinnedHeader ? headerHeight : "xl"} /> */ }
          <Outlet />
          <Affix
            position={ {
              bottom: 20,
              right: 20,
            } }
          >
            <Transition
              transition="slide-up"
              mounted={ scroll.y > 0 }
            >
              { transitionStyles => (
                <Button
                  leftSection={ <NorthIcon /> }
                  style={ transitionStyles }
                  onClick={ () => scrollTo({
                    y: 0,
                  }) }
                >
                  Scroll to top
                </Button>
              ) }
            </Transition>
          </Affix>
        </AppShell.Main>
        <AppShell.Aside
          ref={ asideRef }
          className={ styles.layoutAside }
          p="0"
        >
          <Stack
            align="stretch"
            justify="center"
            gap="xs"
            p="xs"
          >
            <UnstyledButton
              aria-label="Toggle right navigation menu opened"
              onClick={ () => {
                asideDesktopHandlers.toggle();
                asideMobileHandlers.toggle();
              } }
            >
              <Group
                preventGrowOverflow={ false }
                wrap="nowrap"
                align="center"
                justify="start"
                pr="xl"
              >
                <BackgroundLetterAvatar
                  username={ `${auth.user?.profile?.given_name} ${auth.user?.profile?.family_name}` }
                />
                <Text
                  span
                  c="var(--mantine-color-white)"
                  size="md"
                  fw={ 800 }
                  ml="1em"
                >
                  { auth.user?.profile.given_name }
                  { NonBreakingCharEnum.space }
                  { auth.user?.profile.family_name }
                </Text>
              </Group>
            </UnstyledButton>
          </Stack>
          <Space
            h="sm"
          />
          <Divider
            size="md"
            color="primary.6"
          />
          <Space
            h="lg"
          />
          <AuthenticatedAside
            { ...{isAdmin} }
          />
        </AppShell.Aside>
      </AppShell>
    </>
  );
};
const authProps: WithAuthenticationRequiredProps = {
  OnRedirecting: () => (<div>Redirecting to the login page...</div>),
};

const AuthRequiredComponent = withAuthenticationRequired(
  protectedOutlet,
  authProps,
);

function isSetupComplete(
  auth: RootRouterAuthContext,
): boolean {
  if (auth.isLoading)  {
    // Wait until authentication result is loaded to deterimine if setup has been completed
    return true;
  } else {
    if (auth.isAuthenticated) {
      const accountant = auth?.user?.profile?.accountant;
      console.debug('User profile accountant', accountant);
      if (accountant) {
        return (
          accountant.verified === 'True'
          && Object(accountant).hasOwnProperty('ssid')
        );
      }
    }
  }
  return false;
}


export const Route = createFileRoute('/_app/_authenticated')({
  component: RouteComponent,
  notFoundComponent: NotFoundError,
});

function RouteComponent(): React.JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isSetupComplete(auth as RootRouterAuthContext)) {
      console.warn(`Setup not complete, navigating to ATO setup form`);
      navigate({
        to: '/setup',
        // throw: true,
      });
    }
  }, [
    auth.isLoading,
  ]);
  // User idle detection
  const userSessionIdle = useIdle(
    (14 * 60 * 1000), // 14 Minutes in milliseconds
    {
      initialState: false,
    },
  );
  const [
    autoLogoutDeadline,
    setAutoLogoutDeadline,
  ] = useState<number>(Number.MAX_SAFE_INTEGER);
  const [
    secondsRemaining,
    setSecondsRemaining,
  ] = useState<number>(sessionIdleWarningSeconds);
  const [
    modalId,
    setModalId,
  ] = useState<string>('');
  const {
    start: startTimeout,
    clear: clearTimeout,
  } = useTimeout(() => {
    console.info('Automatic Logout triggered');
    auth.signoutRedirect();
  }, (10 * 60 * 1000));
  const secondsUpdateInterval = useInterval(() => {
    const diff: number = autoLogoutDeadline - Date.now();
    if (diff > 0) {
      setSecondsRemaining(
        Math.floor(diff / 1000),
      );
      modals.updateModal({
        modalId,
        children: IdleSessionModal({
          secondsRemaining,
        }),
      });
    } else {
      auth.signoutRedirect();
    }
  }, 1000);
  // User idle warning modal and logout timeout
  useEffect(() => {
    if (auth.isAuthenticated) {
      if (userSessionIdle) {
        const modalOpenDateTime = Date.now();
        setAutoLogoutDeadline(modalOpenDateTime + (1000 * sessionIdleWarningSeconds));
        startTimeout();
        if (!secondsUpdateInterval.active) {
          secondsUpdateInterval.start();
        }
        const id = modals.open({
          size: 'xl',
          radius: 'xl',
          keepMounted: true,
          title: 'Extend your session?',
          children: IdleSessionModal({
            secondsRemaining,
          }),
          withCloseButton: false,
          closeOnClickOutside: true,
          closeOnEscape: true,
          centered: true,
          bg: 'var(--mantine-color-primary-9)',
          right: 0,
          onClose() {
            clearTimeout();
            console.debug(
              "Idle signout canceled by user, renewing session token",
              auth.querySessionStatus(),
            )
            auth.signinSilent(
            ).then(() => {
              console.log('Silent token renewal started.');
            }).catch(error => {
                console.error('Error during silent renew:', error);
            });
          },
        });
        setModalId(id);
      } else {
        if (secondsUpdateInterval.active) {
          secondsUpdateInterval.stop();
        }
      }
    }
  }, [
    userSessionIdle,
    auth.isAuthenticated,
  ]);

  useEffect(() => {
    if (auth.activeNavigator) {
      console.debug(`Active auth navigator: ${auth.activeNavigator}`);
    }
  }, [auth.activeNavigator]);
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 1);
  return (
    <AuthRequiredComponent />
  );
}
